import { DateTime } from "luxon"

import type { TranslationKey } from "../../../modules/localization/translations"

export type DatePickerRange = "past" | "past-to-now" | "future" | "now-to-future" | "all"

export type DateTimePickerRange = "past" | "future" | "all"

type DateConstraints = Readonly<{
    minDate?: DateTime
    maxDate?: DateTime
}>

type DateTimeConstraints = Readonly<{
    minDateTime?: DateTime
    maxDateTime?: DateTime
}>

export function getDateRangeConstraints(range: DatePickerRange): DateConstraints {
    const today = DateTime.local().startOf("day")

    switch (range) {
        case "past":
            return { maxDate: today.minus({ days: 1 }) }
        case "past-to-now":
            return { maxDate: today }
        case "future":
            return { minDate: today.plus({ days: 1 }) }
        case "now-to-future":
            return { minDate: today }
        case "all":
            return {}
    }
}

export function getDateTimeRangeConstraints(range: DateTimePickerRange): DateTimeConstraints {
    const now = DateTime.now()

    switch (range) {
        case "past":
            return { maxDateTime: now }
        case "future":
            return { minDateTime: now }
        case "all":
            return {}
    }
}

export function validateDateRange(value: DateTime | null, range: DatePickerRange): TranslationKey | undefined {
    if (!value || !value.isValid) return undefined

    const today = DateTime.local().startOf("day")
    const valueDay = value.startOf("day")

    switch (range) {
        case "past":
            if (valueDay >= today) return "validation-date-must-be-in-past"
            break
        case "past-to-now":
            if (valueDay > today) return "validation-date-must-be-today-or-past"
            break
        case "future":
            if (valueDay <= today) return "validation-date-must-be-in-future"
            break
        case "now-to-future":
            if (valueDay < today) return "validation-date-must-be-today-or-future"
            break
        case "all":
            break
    }

    return undefined
}

export function validateDateTimeRange(value: DateTime | null, range: DateTimePickerRange): TranslationKey | undefined {
    if (!value || !value.isValid) return undefined

    const now = DateTime.now()

    switch (range) {
        case "past":
            if (value >= now) return "validation-datetime-must-be-in-past"
            break
        case "future":
            if (value <= now) return "validation-datetime-must-be-in-future"
            break
        case "all":
            break
    }

    return undefined
}

export function intersectMinDate(a?: DateTime, b?: DateTime): DateTime | undefined {
    if (!a) return b
    if (!b) return a
    return a > b ? a : b
}

export function intersectMaxDate(a?: DateTime, b?: DateTime): DateTime | undefined {
    if (!a) return b
    if (!b) return a
    return a < b ? a : b
}

